import { useCallback, useState } from 'react';

interface UseSetCallbacks<T> {
  add: (value: T) => void;
  clear: () => void;
  has: (value: T) => boolean;
  delete: (value: T) => boolean;
}

/**
 * useSet
 *
 * State hook that provides a `Set` API. Does not expose the actual data
 * structure; instead, only exposes a getter and setter API.
 *
 * Compared to the native `Set()`, `useSet`'s public API is pure.
 */
export default function <T = any>(initialValue: T[] = []): UseSetCallbacks<T> {
  const [value, setValue] = useState<Set<T>>(new Set<T>(initialValue));
  const add = useCallback((v: T) => setValue(new Set(value.add(v))), [value]);
  const has = useCallback((v: T) => value.has(v), [value]);
  const del = useCallback(
    (v: T) => {
      const res = value.delete(v);
      setValue(new Set(value));
      return res;
    },
    [value],
  );
  const clear = useCallback(() => setValue(new Set()), []);

  return {
    add,
    has,
    delete: del,
    clear,
  };
}
